import mapKeysDeep from '@kikobeats/map-keys-deep';
import { getErrorMsg, logSentry } from 'app/core/errors/error-utils';
import {
  deleteAuthToken,
  getAuthToken,
} from 'app/core/token/token-utils';
import {
  isNonNullable,
  Never$,
  Obj,
  Opt,
} from 'app/tools/general.types';
import {
  keysToCamelCase,
  keysToSnakeCase,
  SafeMap,
} from 'app/tools/utils';
import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import _ from 'lodash';
import mapValuesDeep from 'map-values-deep';
import { pipe, pipeWith } from 'pipe-ts';
import { Observable, OperatorFunction } from 'rxjs';
import { catchError, map, pluck, retryWhen } from 'rxjs/operators';
import { JsonObject } from 'type-fest';
import {
  ApiError,
  ApiErrorWithNonFieldErrors,
  FormDataSource,
  isAxiosError,
  isFormDataSource,
  KeyedApi,
  KeyedApiAxiosError,
  MappedApi,
  RawApi,
  RawApiError,
} from './api-core.types';

//
// ------------------ REQUEST UTILS ------------------

const mapEmptyStringValuesToNull = <T>(data: T): T =>
  mapValuesDeep(data, v => (v === '' ? null : v));

const formatRequestDataKeys = (data: JsonObject | FormDataSource) =>
  pipeWith(
    data,
    d => keysToSnakeCase(d) as JsonObject | FormDataSource,
    d =>
      mapKeysDeep(d, (_value, key) =>
        // NOTE: corrects misc inconsistencies. List of all fixed instances:
        // (X is stand-in for 1 or more numerical digits)
        // • password_X -> passwordX
        // • new_password_X -> new_passwordX
        // • address_X -> addressX
        String(key).replace(/(^address|password)_(\d*)$/, '$1$2')
      )
  );

const formDataFromSource = (source: FormDataSource): FormData =>
  _.entries(source).reduce((formData, [key, value]) => {
    const processedValue =
      typeof value === 'number' ? String(value) : value;
    if (processedValue) formData.append(key, processedValue);
    return formData;
  }, new FormData());

export const mapRequestData = (
  data: JsonObject | FormDataSource,
  options?: ConfigOptions
): JsonObject | FormData =>
  pipeWith(
    data,
    d =>
      options?.keepEmptyStrings ? d : mapEmptyStringValuesToNull(d),
    d => formatRequestDataKeys(d),
    d => (isFormDataSource(d) ? formDataFromSource(d) : d)
  );

export type ConfigOptions = {
  urlParams?: Obj<string>;
  noAuth?: boolean;
  keepEmptyStrings?: boolean;
};
export const getConfig = ({
  noAuth,
  urlParams,
}: ConfigOptions = {}): AxiosRequestConfig => {
  const authHeadersConfig = !noAuth && {
    headers: {
      Authorization: `Token ${getAuthToken() || '[missing]'}`,
    },
  };
  const paramConfig = urlParams && {
    params: formatRequestDataKeys(urlParams),
  };

  return {
    ...paramConfig,
    ...authHeadersConfig,
  };
};

//
// -------------------- ERROR UTILS --------------------

const preprocessErrorRtn = (errorRtn: unknown): Opt<RawApiError> => {
  if (!errorRtn) return undefined;
  if (typeof errorRtn !== 'object') {
    return { detail: String(errorRtn) } as RawApiError;
  }
  return errorRtn as RawApiError;
};

/**
 * Turns each value in the error object from an array into
 * a string with <br> as the delimiter.
 * @param apiError error object input
 * @return processed error object
 */
const mergeApiErrorArrays = ({
  detail,
  ...otherKeys
}: ApiErrorWithNonFieldErrors): ApiError => {
  const apiError: ApiError = {};
  _.entries(otherKeys).forEach(([key, value]) => {
    apiError[key] = value.join('<br>');
  });
  return { detail, ...apiError };
};

/**
 * Simply merges the `nonFieldError` field into the `detail`
 * field, again using <br> as the delimiter.
 * @param apiError error object input
 * @returns the processed error object
 */
const mergeNonFieldErrorsToDetail = ({
  nonFieldErrors: nonFieldErrs,
  detail,
  ...errorKeys
}: ApiError): ApiError => ({
  detail: [nonFieldErrs, detail].filter(isNonNullable).join('<br>'),
  ...errorKeys,
});

// type NestedErrObj<T extends string[] | NestedErrObj<T>> = Obj<T>
// export const flattenErrors = <T extends string[] | NestedErrObj<T>>(
//   errors?: NestedErrObj<T>
// ): Opt<Obj<string[]>> => errors &&
//     _(errors).entries().map(([key, value]) =>
//       _.isArray(value) ? [key, value] : [key, flattenErrors(value)]
//     );

const onErrorPolicy =
  () =>
  (error$: Observable<AxiosError>): Never$ =>
    error$.pipe(
      map(err => {
        type ReqObj = { responseUrl?: string };
        const url = _.get(err.request as ReqObj, 'responseUrl');

        if (
          err.response?.status === 401 &&
          url?.includes('iwarranty')
        ) {
          deleteAuthToken();
        }
        throw err;
      })
    );

const mapApiError = (
  error: unknown
): unknown | KeyedApiAxiosError => {
  if (!isAxiosError(error)) {
    return error;
  }
  const headers = new SafeMap(
    error.response?.headers as Opt<Obj<Opt<string>>>
  );
  if (headers.get('content-type')?.includes('text/html')) {
    return error;
  }
  const defaultRtn = {
    detail: getErrorMsg(error),
  } as ApiErrorWithNonFieldErrors;

  const rawRtn = preprocessErrorRtn(error.response?.data);
  const keyMappedRtn =
    rawRtn &&
    // NOTE: Typescript can't handle this, hence typecasting
    (keysToCamelCase(rawRtn) as ApiErrorWithNonFieldErrors);
  // improve missing field error message
  const fullyMappedRtn = mapValuesDeep(keyMappedRtn, v => {
    if (typeof v === 'string') {
      return v.replace('may not be null', 'may not be empty');
    } else {
      return v;
    }
  });

  const processedRtn = pipeWith(
    fullyMappedRtn || defaultRtn,
    mergeApiErrorArrays,
    mergeNonFieldErrorsToDetail
  );

  const formattedError = error as KeyedApiAxiosError;
  formattedError.data = processedRtn;
  formattedError.isKeyedAxiosError = true;
  return formattedError;
};

// ------------------- RESPONSE UTILS -------------------

const manageAxiosResponseRaw =
  <T>(): OperatorFunction<AxiosResponse<T>, T> =>
  response$ =>
    response$.pipe(retryWhen(onErrorPolicy()), pluck('data'));

const dataMapping = <T>(data: KeyedApi<T>) =>
  mapValuesDeep<KeyedApi<T>, MappedApi<T>>(
    data,
    pipe(
      // replaceCase every 'null' value with 'undefined'.
      item => item ?? undefined,
      // upgrade any url with http to https to avoid mixed content errors
      item =>
        typeof item === 'string'
          ? item.replace(/^http:\/\//, 'https://')
          : item
    )
  );

export const manageResponseData =
  <T>(): OperatorFunction<RawApi<T>, MappedApi<T>> =>
  data$ =>
    data$.pipe(
      map(keysToCamelCase),
      map(dataMapping),
      catchError((e: unknown) => {
        const mappedError = mapApiError(e);

        if (isAxiosError(e)) {
          const foundMsg = getErrorMsg(e) || [];
          const apiMsgArr = [
            e.response?.status || [],
            e.message || [],
            foundMsg !== e.message ? foundMsg : [],
          ];
          e.message = apiMsgArr.flat().join(' | ');
        }
        logSentry(e);
        throw mappedError;
      })
    );

export const manageAxiosResponse =
  <T>(): OperatorFunction<AxiosResponse<RawApi<T>>, MappedApi<T>> =>
  response$ =>
    response$.pipe(manageAxiosResponseRaw(), manageResponseData());
